/**
 * PermissionDenied — calm explanation for a permission boundary (Design System §5)
 *
 * A permission boundary is a normal state, not a failure: it explains what is
 * out of reach, which permission unlocks it, and where the user can safely go
 * instead. Never shows raw authorization errors and never dead-ends.
 */

import React from 'react';
import { t } from '@budgeting101/i18n';

export interface PermissionDeniedProps {
  /** What is out of reach, in the user's terms, e.g. "Team budgets". */
  subject: string;
  /** Permission that unlocks it, e.g. "budgets.view". Shown verbatim. */
  requiredPermission?: string;
  /** Safe explanation, e.g. who can grant access or why the boundary exists. */
  explanation?: string;
  /** Safe destination actions (e.g. back to dashboard, request access). */
  actions?: React.ReactNode;
  className?: string;
}

export function PermissionDenied({
  subject,
  requiredPermission,
  explanation,
  actions,
  className,
}: PermissionDeniedProps) {
  return (
    <div
      className={['ui-permission-denied', className ?? ''].filter(Boolean).join(' ')}
      style={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        gap: 'var(--space-3)',
        padding: 'var(--space-8) var(--space-6)',
        backgroundColor: 'var(--color-surface-subtle)',
        border: 'var(--border-width-default) solid var(--color-border)',
        borderRadius: 'var(--radius-md)',
        textAlign: 'center',
      }}
    >
      <span
        aria-hidden="true"
        className="material-symbols-outlined"
        style={{ fontSize: '32px', lineHeight: 1, color: 'var(--color-text-disabled)' }}
      >
        lock
      </span>

      <span
        className="ui-permission-denied__title"
        style={{
          fontSize: 'var(--text-body-strong-size)',
          fontWeight: 'var(--text-body-strong-weight)',
          color: 'var(--color-text)',
        }}
      >
        {t('ui.permissionDeniedTitle', { subject })}
      </span>

      {explanation && (
        <p
          className="ui-permission-denied__explanation"
          style={{
            margin: 0,
            fontSize: 'var(--text-body-size)',
            lineHeight: 'var(--text-body-lh)',
            color: 'var(--color-text-secondary)',
            maxWidth: '360px',
          }}
        >
          {explanation}
        </p>
      )}

      {requiredPermission && (
        <span
          className="ui-permission-denied__permission"
          style={{
            fontSize: 'var(--text-caption-size)',
            lineHeight: 'var(--text-caption-lh)',
            color: 'var(--color-text-secondary)',
          }}
        >
          {t('ui.permissionDeniedRequired')} <code style={{ fontFamily: 'inherit' }}>{requiredPermission}</code>
        </span>
      )}

      {actions && (
        <div style={{ display: 'flex', flexWrap: 'wrap', justifyContent: 'center', gap: 'var(--space-3)' }}>
          {actions}
        </div>
      )}
    </div>
  );
}
